/**
 * @deprecated User/Admin displays use useSiteDates; retained for Service UI consumers.
 * Format date to YYYY-MM-DD HH:mm:ss (24h)
 */
export function formatDateTime(date) {
    if (!date)
        return '-';
    const d = new Date(date);
    if (isNaN(d.getTime()))
        return '-';
    const pad = (n) => n.toString().padStart(2, '0');
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
export const SITE_DATE_FORMATS = ['iso', 'local'];
/** The stored format, read from `site_config.settings`; anything absent or unknown reads `iso`. */
export function readSiteDateFormat(settings) {
    const ui = settings && typeof settings === 'object' ? settings.ui : null;
    const format = ui && typeof ui === 'object' ? ui.date_format : null;
    return format === 'local' ? 'local' : 'iso';
}
function siteZone(timeZone) {
    if (!timeZone)
        return undefined;
    try {
        new Intl.DateTimeFormat('en', { timeZone });
        return timeZone;
    }
    catch {
        return undefined;
    }
}
function localeFor(locale) {
    return locale === 'en' || locale.startsWith('en-') ? 'en-GB' : locale;
}
function validDate(value) {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/** A comparable calendar day in the site zone; UTC arithmetic survives DST changes. */
export function siteCalendarDay(value, timeZone) {
    const date = validDate(value);
    if (!date)
        return NaN;
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: siteZone(timeZone), year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(date);
    const part = (type) => Number(parts.find((p) => p.type === type)?.value);
    return Date.UTC(part('year'), part('month') - 1, part('day')) / 86_400_000;
}
/** Pure printers shared by app hooks without importing React or the common root barrel. */
export function createSiteDates(options, words) {
    const print = (value, formatter) => value == null || value === '' ? '—' : formatter(value, options) || '—';
    const dateTime = (value) => print(value, formatSiteDateTime);
    return {
        options,
        date: (value) => print(value, formatSiteDate),
        dateTime,
        stamp: (value, now = new Date()) => {
            if (value == null || value === '')
                return '—';
            const ago = siteCalendarDay(now, options.timeZone) - siteCalendarDay(value, options.timeZone);
            if (ago === 0 || ago === 1) {
                return `${ago === 0 ? words.today : words.yesterday} ${formatSiteTime(value, options)}`;
            }
            return dateTime(value);
        },
    };
}
/** `HH:MM`, 24 h, in the site's zone. */
export function formatSiteTime(value, options) {
    const date = validDate(value);
    if (!date)
        return '';
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: siteZone(options.timeZone), hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(date);
}
/** A calendar date in the site's zone: `2026-10-22` (iso) or `22 Oct 2026` / `22. lis 2026.` (local). */
export function formatSiteDate(value, options) {
    const date = validDate(value);
    if (!date)
        return '';
    const timeZone = siteZone(options.timeZone);
    if (options.format === 'local') {
        return new Intl.DateTimeFormat(localeFor(options.locale), { timeZone, day: 'numeric', month: 'short', year: 'numeric' })
            .format(date);
    }
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(date);
    const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
}
/** Date and time: `2026-10-22 14:22` (iso) or `22 Oct 2026, 14:22` / `22. lis 2026. 14:22` (local). */
export function formatSiteDateTime(value, options) {
    const day = formatSiteDate(value, options);
    if (!day)
        return '';
    const time = formatSiteTime(value, options);
    if (options.format === 'iso')
        return `${day} ${time}`;
    return day.endsWith('.') ? `${day} ${time}` : `${day}, ${time}`;
}
//# sourceMappingURL=date.js.map